import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  hydrateSubscriptionsThunk,
  selectSubscriptionHydrationFailed,
} from '@common-reducers/subscription-hydration';
import { AlertTriangle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * FE#167 — the explicit failure state for the entitlement fetch.
 *
 * The gates hold while entitlement is `unknown`, which is correct but silent. When the
 * subscription fetch has exhausted its bounded retries the customer would otherwise sit
 * against an inert control with no explanation and no way forward. This says what
 * happened and offers the only safe action: try again.
 *
 * It deliberately states NOTHING about the plan. Naming a tier here would be the very
 * guess this whole change exists to remove — we do not know the entitlement, that is
 * why the dialog is on screen.
 *
 * Rendered by the BLOCKING gates only (scheduling composer, engine quota). Cosmetic
 * upsells simply stay suppressed; interrupting someone with a modal because a banner
 * could not be decided would be worse than the banner's absence.
 */
export const EntitlementErrorDialog: React.FC<{
  /** Only surface while the user is actually looking at a held control. */
  open?: boolean;
  onClose?: () => void;
}> = ({ open = true, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const hydrationFailed = useAppSelector(selectSubscriptionHydrationFailed);
  const [retrying, setRetrying] = React.useState(false);

  const show = open && hydrationFailed;

  const onRetry = async () => {
    setRetrying(true);
    try {
      await dispatch(hydrateSubscriptionsThunk({ isRetry: true }) as any);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Dialog open={show} onOpenChange={(next: boolean) => { if (!next) onClose?.(); }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" style={{ color: 'var(--status-warning)' }} />
            {t('entitlementError.title', 'Something went wrong')}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {t(
              'entitlementError.description',
              "We couldn't load your plan details. Check your connection and try again.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => onClose?.()} disabled={retrying}>
            {t('common.close', 'Close')}
          </Button>
          <Button size="sm" onClick={onRetry} disabled={retrying}>
            {retrying
              ? t('entitlementError.retrying', 'Retrying…')
              : t('entitlementError.retry', 'Retry')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EntitlementErrorDialog;
